import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  PlayCircle, Download, Lock, Crown, Ticket, Film,
  ShoppingCart, BarChart3, CheckCircle2, ArrowRight, Handshake,
} from 'lucide-react';
import statsThumbnail from '@/assets/stats-thumbnail.png';
import { toast } from 'sonner';
import {
  buildAssetUrl, formatMoney, friendlyError,
  startContentCheckout, startContentDownloadCheckout,
  fetchVideoDownload, downloadFromUrlWithProgress,
  confirmContentDownloadComplete, releaseContentDownload,
  fetchMyDownloads,
  api,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const KIND_LABEL = {
  highlights: 'Highlights',
  full_match: 'Partido completo',
  summary: 'Resumen',
  interview: 'Entrevista',
  statistics: 'Estadísticas',
  other: 'Otro',
};

const BUY_LABEL = {
  highlights: 'Comprar highlights',
  full_match: 'Comprar este partido',
  summary: 'Comprar el resumen',
  interview: 'Comprar la entrevista',
  statistics: 'Comprar las estadísticas',
  other: 'Comprar este contenido',
};

// Pill compacta (una sola línea) que resume el estado comercial de este
// contenido. Sustituye a los bloques voluminosos "Compra individual" /
// "Incluido con Premium". Reutiliza íntegramente los datos de access/.
function AccessPill({ video }) {
  const hasAccess = Boolean(video?.access?.access);
  const reason = (video?.access?.reason || '').toString();
  const price = formatMoney(video.price_cents, video.currency);

  if (hasAccess) {
    if (video.access_type === 'free' || reason === 'free') {
      return (
        <span
          className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-emerald-500/10 text-emerald-700 text-xs font-semibold"
          data-testid={`public-content-pill-${video.id}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Gratis
        </span>
      );
    }
    if (reason === 'entitlement:platform' || reason === 'entitlement:club') {
      return (
        <span
          className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] text-xs font-semibold"
          data-testid={`public-content-pill-${video.id}`}
        >
          <Crown className="h-3.5 w-3.5" /> Premium
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-emerald-500/10 text-emerald-700 text-xs font-semibold"
        data-testid={`public-content-pill-${video.id}`}
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> Tu acceso
      </span>
    );
  }

  // Sin acceso aún.
  if (video.access_type === 'free') {
    return (
      <span
        className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-emerald-500/10 text-emerald-700 text-xs font-semibold"
        data-testid={`public-content-pill-${video.id}`}
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> Gratis
      </span>
    );
  }
  if (video.access_type === 'premium') {
    return (
      <span
        className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] text-xs font-semibold"
        data-testid={`public-content-pill-${video.id}`}
      >
        <Crown className="h-3.5 w-3.5" /> Incluido en Premium
      </span>
    );
  }
  // pay_per_content | premium_or_pay_per_content → mostrar precio compacto.
  return (
    <span
      className="inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-[var(--ns-gold)]/15 text-[var(--ns-gold-700)] text-xs font-semibold"
      data-testid={`public-content-pill-${video.id}`}
    >
      <Lock className="h-3.5 w-3.5" /> PPV {price}
    </span>
  );
}

function accessTag(v) {
  switch (v.access_type) {
    case 'free': return 'Gratis';
    case 'premium': return 'Premium';
    case 'pay_per_content': return `${formatMoney(v.price_cents, v.currency)}`;
    case 'premium_or_pay_per_content': return `Premium o ${formatMoney(v.price_cents, v.currency)}`;
    default: return null;
  }
}

function replayCTA(v, isAuthenticated, onBuy, eventSlug) {
  const a = v.access || {};
  const watchUrl = `/eventos/${eventSlug}/watch?v=${v.id}`;
  const isStats = v.content_kind === 'statistics';
  // Acceso concedido → Reproducir (o Ver estadísticas si statistics).
  if (a.access) {
    if (isStats) {
      return { label: 'Ver estadísticas', icon: BarChart3, action: 'watch', to: watchUrl };
    }
    if (v.access_type === 'free') {
      return { label: 'Ver ahora', icon: PlayCircle, action: 'watch', to: watchUrl };
    }
    return { label: 'Reproducir', icon: PlayCircle, action: 'watch', to: watchUrl };
  }
  // Solo Premium → Ver con Premium.
  if (v.access_type === 'premium') {
    return { label: 'Ver con Premium', icon: Crown, action: 'plans', to: '/planes' };
  }
  // PPC puro → Desbloquear por X.
  if (v.access_type === 'pay_per_content') {
    return {
      label: `Desbloquear por ${formatMoney(v.price_cents, v.currency)}`,
      icon: Ticket, action: 'buy', onClick: onBuy,
    };
  }
  // Híbrido Premium o PPC → Desbloquear por X (no Premium).
  if (v.access_type === 'premium_or_pay_per_content') {
    return {
      label: `Desbloquear por ${formatMoney(v.price_cents, v.currency)}`,
      icon: Ticket, action: 'buy', onClick: onBuy,
    };
  }
  return null;
}

function downloadCTA(v, myDownloadsByEnt = {}) {
  const d = v.download || {};
  if (!v.download_mode || v.download_mode === 'none') return null;
  // Fase 06D — consulta /me/downloads: si el usuario tiene un entitlement
  // de descarga para este contenido, su estado manda sobre el CTA. Esto
  // oculta el botón `buyDownload` (y su precio) cuando ya se ha pagado.
  const entId = d.entitlement_id || null;
  const myEnt = entId ? myDownloadsByEnt[entId] : null;
  if (myEnt) {
    const st = myEnt.status;
    const ready = Boolean(myEnt.ready_to_download && myEnt.download_asset_id);
    if (st === 'exhausted') {
      return { kind: 'done', label: 'Descarga realizada' };
    }
    if (st === 'active' && !ready) {
      return { kind: 'preparing', label: 'Descarga en preparación' };
    }
    if (st === 'active' && ready) {
      return { kind: 'download_free', label: 'Descargar ahora' };
    }
    // `expired` / `revoked` → cae a la lógica original (sin ent activo).
  }
  if (d.state === 'included' && d.available) {
    if (v.access_type === 'free' && v.download_mode === 'included_with_access') {
      return { label: 'Descargar gratis', kind: 'download_free' };
    }
    return { label: 'Descargar', kind: 'download_free' };
  }
  if (d.state === 'owned' && d.available) {
    return { label: 'Descargar', kind: 'download_free' };
  }
  // Bypass administrativo (admin/super_admin): el backend autoriza la
  // descarga sin entitlement y marca `state=admin_bypass` + available=true.
  // Reutilizamos el CTA de descarga directa; el endpoint legacy ya
  // devuelve `requires_confirmation=false`, por lo que no se consumen
  // entitlements ni se tocan contadores comerciales.
  if (d.state === 'admin_bypass' && d.available) {
    return { label: 'Descargar', kind: 'download_free' };
  }
  if (d.state === 'must_buy') {
    return { label: `Descargar por ${formatMoney(d.price_cents, d.currency)}`, kind: 'buy_download' };
  }
  // --- Nuevos casos informativos (sin botón de compra activo) ---
  // Se renderizan dentro de la columna derecha de opciones comerciales
  // para que la descarga de pago sea visible también antes de adquirir
  // acceso al replay. No alteran el comportamiento de must_buy/owned/
  // included, que siguen generando el bloque inferior con botón real.
  if (d.state === 'requires_login' || d.state === 'must_buy_replay_first') {
    return {
      kind: 'info_locked',
      state: d.state,
      price: formatMoney(d.price_cents, d.currency),
    };
  }
  return null;
}

function ContentCard({ video, eventSlug, refresh, myDownloadsByEnt = {}, previewVideoUrl, posterUrl, eventId, onPreviewPlay }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [downloadStatus, setDownloadStatus] = React.useState('idle');
  const [downloadProgress, setDownloadProgress] = React.useState(0);
  const watchUrl = `/eventos/${eventSlug}/watch?v=${video.id}`;
  const nextEncoded = encodeURIComponent(watchUrl);

  const buyReplay = async () => {
    if (!user) {
      toast.info('Inicia sesión para comprar este contenido.');
      navigate(`/acceder?next=${nextEncoded}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await startContentCheckout(video.id);
      navigate(`/checkout/sesion/${data.id}?next=${nextEncoded}`);
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setBusy(false); }
  };

  const buyDownload = async () => {
    // Tras la compra de descarga volvemos a la ficha del partido con
    // ancla al bloque de contenidos para que el botón "Descargar ahora"
    // aparezca ya visible junto a su contenido. El `compra=descarga`
    // permite a EventDetail mostrar el estado de confirmación de compra.
    const downloadNextUrl = `/eventos/${eventSlug}?compra=descarga#contenidos`;
    const downloadNextEncoded = encodeURIComponent(downloadNextUrl);
    if (!user) {
      toast.info('Inicia sesión para comprar la descarga.');
      navigate(`/acceder?next=${downloadNextEncoded}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await startContentDownloadCheckout(video.id);
      navigate(`/checkout/sesion/${data.id}?next=${downloadNextEncoded}`);
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setBusy(false); }
  };

  const downloadNow = async () => {
    if (busy || downloadStatus === 'completed') return;
    setBusy(true);
    setDownloadStatus('preparing');
    setDownloadProgress(0);
    let started = false;
    try {
      const data = await fetchVideoDownload(video.id);
      if (!data?.download_url) {
        throw new Error('Este contenido no tiene descarga disponible.');
      }
      started = true;
      // El backend indica expresamente si esta descarga consume entitlement
      // y por tanto requiere confirmación comercial. Para descargas
      // gratuitas o incluidas (Premium) `requires_confirmation` es false y
      // NO se llama a /complete ni /release.
      const requiresConfirmation = data.requires_confirmation === true;
      const fallback = `${(video.title || 'contenido').replace(/\s+/g, '_')}.mp4`;
      await downloadFromUrlWithProgress(
        data.download_url, data.filename || fallback,
        {
          onProgress: (pct, st) => {
            setDownloadProgress(pct);
            if (st) setDownloadStatus(st);
          },
        },
      );
      if (requiresConfirmation) {
        await confirmContentDownloadComplete(video.id);
      }
      setDownloadStatus('completed');
      toast.success('Descarga completada.');
      if (typeof refresh === 'function') {
        setTimeout(() => refresh(), 300);
      }
    } catch (err) {
      setDownloadStatus('error');
      setDownloadProgress(0);
      // Solo liberar si realmente consume entitlement y hubo descarga iniciada.
      if (started) {
        try {
          // Best-effort: la propia función ya ignora 404; además comprobamos
          // la señal del backend para no llamar en descargas sin consumo.
          await releaseContentDownload(video.id);
        } catch { /* noop */ }
      }
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  };

  const replay = replayCTA(video, !!user, buyReplay, eventSlug);
  const download = downloadCTA(video, myDownloadsByEnt);
  const hasAccess = Boolean(video?.access?.access);
  const accessReason = (video?.access?.reason || '').toString();
  const showPremiumUpsell = hasAccess && (
    accessReason === 'entitlement:content'
    || accessReason === 'entitlement:event'
  ) && !user?.has_active_subscription;
  // Enlace secundario "Ver planes →" sólo en el caso híbrido PPC+Premium y
  // cuando el usuario aún no tiene acceso. Es la equivalencia compacta del
  // antiguo bloque "Incluido con Premium".
  const showPlansHint = !hasAccess && (
    video.access_type === 'premium_or_pay_per_content'
  );
  // Mostramos la fila "Compra individual" (etiqueta + precio + botón)
  // únicamente cuando el usuario todavía no tiene acceso y existe esa
  // opción comercial. En ese caso ocultamos la AccessPill superior para
  // no duplicar el precio "PPV X,XX €".
  const showBuyRow = !hasAccess && (
    video.access_type === 'pay_per_content'
    || video.access_type === 'premium_or_pay_per_content'
  );
  const isStats = video.content_kind === 'statistics';
  const kindLabel = KIND_LABEL[video.content_kind] || video.content_kind;

  // Metadatos pie (publicación / duración), 1 línea.
  const publishedAt = video.published_at || video.created_at;
  const pubStr = publishedAt
    ? new Date(publishedAt).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'short', year: 'numeric',
    })
    : null;
  const durationSec = Number(video.duration_seconds || 0);
  let durStr = null;
  if (durationSec > 0) {
    const h = Math.floor(durationSec / 3600);
    const m = Math.floor((durationSec % 3600) / 60);
    durStr = h > 0 ? `${h}h ${m}min` : `${m}min`;
  }

  const isFullMatch = video.content_kind === 'full_match';
  const hasPreview = isFullMatch && !!previewVideoUrl;
  const previewRef = React.useRef(null);
  const previewTickedRef = React.useRef(false);
  const [previewDuration, setPreviewDuration] = React.useState(null);
  const handlePreviewPlay = React.useCallback(() => {
    // Tick atómico una sola vez por carga de página. Fire-and-forget:
    // un fallo HTTP no interrumpe la reproducción ni afecta a nada más.
    if (previewTickedRef.current || !eventId) return;
    previewTickedRef.current = true;
    try {
      api.post(`/events/${encodeURIComponent(eventId)}/preview-view`).catch(() => {});
    } catch { /* noop */ }
  }, [eventId]);

  return (
    <Card
      className="bg-white border border-[var(--ns-border)] overflow-hidden"
      data-testid={`public-content-card-${video.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[11rem_minmax(0,1fr)_14rem] gap-3 p-3">
        {/* 1 · Miniatura compacta (o preview inline si es "Partido completo" y hay preview_video_url) */}
        <div className="md:w-44 w-full">
          {hasPreview ? (
            <div className="space-y-1">
              <div
                className="aspect-video rounded-lg overflow-hidden bg-black relative"
                data-testid={`public-content-preview-${video.id}`}
              >
                <video
                  ref={previewRef}
                  src={previewVideoUrl}
                  poster={posterUrl ? buildAssetUrl(posterUrl) : undefined}
                  controls
                  preload="metadata"
                  playsInline
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onPlay={handlePreviewPlay}
                  onLoadedMetadata={(e) => {
                    const d = e.currentTarget && e.currentTarget.duration;
                    if (d && Number.isFinite(d) && d > 0) setPreviewDuration(Math.round(d));
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full h-full object-cover bg-black"
                  data-testid={`public-content-preview-video-${video.id}`}
                />
                <span
                  className="pointer-events-none select-none absolute top-1.5 left-1.5 inline-flex items-center gap-1 h-5 px-1.5 rounded bg-black/70 text-[10px] font-semibold uppercase tracking-wide text-white"
                  data-testid={`public-content-preview-badge-${video.id}`}
                >
                  Vista previa{previewDuration ? ` · ${previewDuration} s` : ''}
                </span>
              </div>
              <p
                className="text-[11px] text-[var(--ns-text-muted)]"
                data-testid={`public-content-preview-caption-${video.id}`}
              >
                Adelanto gratuito del partido
              </p>
            </div>
          ) : (
          <Link
            to={watchUrl}
            className="group block aspect-video rounded-lg overflow-hidden bg-[var(--ns-bg-2)] relative"
          >
            {video.thumbnail_url ? (
              <img
                src={buildAssetUrl(video.thumbnail_url)}
                alt={video.title}
                className="w-full h-full object-cover"
              />
            ) : isStats ? (
              <img
                src={statsThumbnail}
                alt={video.title || 'Estadísticas del partido'}
                className="w-full h-full object-cover"
                data-testid={`public-content-stats-thumb-${video.id}`}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[var(--ns-text-subtle)]">
                <Film className="h-6 w-6" />
              </div>
            )}
            {!isStats ? (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                <PlayCircle className="h-9 w-9 text-white opacity-0 group-hover:opacity-90 transition-opacity drop-shadow" />
              </div>
            ) : null}
            {durStr ? (
              <span className="absolute bottom-1.5 right-1.5 inline-flex items-center h-5 px-1.5 rounded bg-black/70 text-[11px] font-semibold text-white">
                {durStr}
              </span>
            ) : null}
          </Link>
          )}
        </div>

        {/* 2 · Info central */}
        <div className="min-w-0 flex flex-col justify-center">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <Badge
              className="h-5 px-1.5 bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] inline-flex items-center gap-1 text-[11px]"
              data-testid={`public-content-kind-badge-${video.id}`}
            >
              {isStats ? <BarChart3 className="h-3 w-3" aria-hidden /> : <Film className="h-3 w-3" aria-hidden />}
              {kindLabel}
            </Badge>
            {/* Nota: el estado comercial (Gratis/Premium/PPV) se muestra sólo
                en la pill derecha (`AccessPill`) para evitar duplicación. */}
          </div>
          <h3
            className="font-semibold text-[var(--ns-text)] text-base leading-snug line-clamp-2"
            data-testid={`public-content-title-${video.id}`}
          >
            {video.title}
          </h3>
          {video.description ? (
            <p className="text-xs text-[var(--ns-text-muted)] mt-1 line-clamp-2">
              {video.description}
            </p>
          ) : null}
          {(pubStr || durStr) ? (
            <p className="text-[11px] text-[var(--ns-text-muted)] mt-1.5">
              {pubStr ? `Publicado: ${pubStr}` : null}
              {pubStr && durStr ? '  ·  ' : null}
              {durStr ? `Duración: ${durStr}` : null}
            </p>
          ) : null}
        </div>

        {/* 3 · CTA derecha compacta */}
        <div className="flex flex-col items-stretch justify-center gap-1.5 md:w-56 md:ml-auto">
          {showBuyRow ? null : <AccessPill video={video} />}
          {hasAccess || video.access_type === 'free' ? (
            replay ? (
              replay.to ? (
                <Button
                  asChild
                  className={`h-9 rounded-lg font-semibold ${isStats
                    ? 'bg-transparent border border-[var(--ns-gold)] text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/10'
                    : 'bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white'}`}
                  data-testid={`public-content-replay-${video.id}`}
                >
                  <Link to={replay.to}>
                    <replay.icon className="h-4 w-4 mr-1.5" />{replay.label}
                  </Link>
                </Button>
              ) : (
                <Button
                  onClick={replay.onClick} disabled={busy}
                  className="h-9 rounded-lg bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold"
                  data-testid={`public-content-replay-${video.id}`}
                >
                  <replay.icon className="h-4 w-4 mr-1.5" />
                  {busy ? 'Procesando…' : replay.label}
                </Button>
              )
            ) : (
              <Button disabled variant="outline" className="h-9 rounded-lg">
                <Lock className="h-4 w-4 mr-1.5" /> No disponible
              </Button>
            )
          ) : (
            <>
              {/* Fila 1 — Compra individual (sólo si está disponible) */}
              {(video.access_type === 'pay_per_content'
                || video.access_type === 'premium_or_pay_per_content') ? (
                <div
                  className="flex flex-col gap-1"
                  data-testid={`public-content-buy-row-${video.id}`}
                >
                  <div className="flex items-center justify-between gap-2 text-[11px] leading-none">
                    <span className="font-semibold text-[var(--ns-text-muted)]">
                      Compra individual
                    </span>
                    <span
                      className="font-semibold text-[var(--ns-gold-700)]"
                      data-testid={`public-content-buy-row-price-${video.id}`}
                    >
                      {formatMoney(video.price_cents, video.currency)}
                    </span>
                  </div>
                  <Button
                    onClick={buyReplay}
                    disabled={busy}
                    className="h-9 rounded-lg bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold"
                    data-testid={`public-content-buy-btn-${video.id}`}
                  >
                    <ShoppingCart className="h-4 w-4 mr-1.5" />
                    {busy ? 'Procesando…' : `Comprar ${formatMoney(video.price_cents, video.currency)}`}
                  </Button>
                </div>
              ) : video.access_type === 'premium' ? (
                <Button
                  asChild
                  className="h-9 rounded-lg bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold"
                  data-testid={`public-content-plans-btn-${video.id}`}
                >
                  <Link to="/planes">
                    <Crown className="h-4 w-4 mr-1.5" /> Ver con Premium
                  </Link>
                </Button>
              ) : null}
              {/* Fila 2 — Incluido con Premium (sólo si showPlansHint) */}
              {showPlansHint ? (
                <div
                  className="flex flex-col gap-1"
                  data-testid={`public-content-premium-row-${video.id}`}
                >
                  <span className="text-[11px] leading-none font-semibold text-[var(--ns-text-muted)]">
                    Incluido con Premium
                  </span>
                  <Button
                    asChild
                    className="h-9 rounded-lg bg-[var(--ns-gold)] hover:bg-[var(--ns-gold-700)] text-white font-semibold"
                    data-testid={`public-content-plans-link-${video.id}`}
                  >
                    <Link to="/planes">
                      <Crown className="h-4 w-4 mr-1.5" /> Acceder con Premium
                    </Link>
                  </Button>
                </div>
              ) : null}
            </>
          )}
          {/* Descarga informativa (sin botón de compra activo) · se muestra
              junto a Compra individual + Premium cuando el usuario aún no
              puede comprar la descarga por sí sola. */}
          {download && download.kind === 'info_locked' ? (
            <div
              className="rounded-lg border border-dashed border-[var(--ns-border)] bg-[var(--ns-bg)]/40 px-2.5 py-1.5"
              data-testid={`public-content-download-info-${video.id}`}
            >
              <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ns-text)]">
                <Download className="h-3 w-3" aria-hidden /> Descarga de pago
              </p>
              <p
                className="text-xs font-semibold text-[var(--ns-gold-700)] leading-tight mt-0.5"
                data-testid={`public-content-download-info-price-${video.id}`}
              >
                {download.price}
              </p>
              <p className="text-[11px] text-[var(--ns-text-muted)] leading-snug mt-0.5">
                Disponible después de adquirir acceso
              </p>
            </div>
          ) : null}
        </div>

        {/* Aviso genérico de apoyo al club · se muestra en TODAS las tarjetas
            de contenido, debajo de la miniatura/preview + info. No incluye el
            nombre del club. Ocupa las dos primeras columnas en desktop para
            no desplazar los CTAs de la derecha. Puramente visual. */}
        <div
          className="md:col-span-2 flex items-start gap-2 rounded-lg bg-[var(--ns-gold)]/10 border border-[var(--ns-gold)]/25 px-2.5 py-2"
          data-testid={`public-content-club-support-notice-${video.id}`}
        >
          <Handshake
            className="h-4 w-4 text-[var(--ns-gold-700)] shrink-0 mt-0.5"
            aria-hidden
          />
          <p className="text-[11px] leading-snug text-[var(--ns-text-muted)]">
            Con tu compra también apoyas a tu club de referencia. Parte del
            importe se comparte con tu club.
          </p>
        </div>
      </div>

      {/* Upsell discreto a Premium tras compra individual · barra inferior. */}
      {showPremiumUpsell ? (
        <div
          className="border-t border-dashed border-[var(--ns-border)] bg-[var(--ns-bg)]/40 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2"
          data-testid={`public-content-premium-upsell-${video.id}`}
        >
          <p className="text-xs text-[var(--ns-text-muted)] flex-1 min-w-0">
            <span className="font-semibold text-[var(--ns-text)]">¿Vas a ver más partidos?</span>{' '}
            Descubre los planes NetScouters y accede a más contenidos con una sola suscripción.
          </p>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="h-8 rounded-lg border-[var(--ns-gold)] text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/10 font-semibold shrink-0"
            data-testid={`public-content-premium-upsell-btn-${video.id}`}
          >
            <Link to="/planes">Ver planes</Link>
          </Button>
        </div>
      ) : null}

      {/* Descarga (independiente del paywall de acceso) · barra inferior
          compacta. Sólo se renderiza para kinds accionables o informativos
          vinculados a un entitlement; el caso `info_locked` vive en la
          columna derecha. */}
      {download && (download.kind === 'download_free' || download.kind === 'buy_download'
        || download.kind === 'preparing' || download.kind === 'done') ? (
        <div
          className="border-t border-[var(--ns-border)] bg-[var(--ns-bg)]/40 px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-2"
        >
          {download.kind === 'preparing' ? (
            <Button
              variant="outline"
              disabled
              className="h-8 rounded-lg border-[var(--ns-border)] text-[var(--ns-text-muted)] text-xs cursor-not-allowed"
              data-testid={`public-content-download-preparing-${video.id}`}
              title="El archivo aún no está preparado."
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Descarga en preparación
            </Button>
          ) : download.kind === 'done' ? (
            <Button
              variant="outline"
              disabled
              className="h-8 rounded-lg border-[var(--ns-border)] text-[var(--ns-text-muted)] text-xs cursor-not-allowed"
              data-testid={`public-content-download-done-${video.id}`}
              title="Ya has descargado este contenido."
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Descarga realizada
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={download.kind === 'download_free' ? downloadNow : buyDownload}
              disabled={busy || downloadStatus === 'completed'}
              className="h-8 rounded-lg border-[var(--ns-border)] text-[var(--ns-text)] hover:bg-[var(--ns-bg)] text-xs"
              data-testid={`public-content-download-${video.id}`}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {downloadStatus === 'preparing' && 'Preparando descarga…'}
              {downloadStatus === 'downloading' && `Descargando ${downloadProgress}%`}
              {downloadStatus === 'completed' && 'Descarga completada'}
              {(downloadStatus === 'idle' || downloadStatus === 'error') && download.label}
            </Button>
          )}
          {(downloadStatus === 'preparing' || downloadStatus === 'downloading' || downloadStatus === 'completed') ? (
            <div
              className="flex-1 min-w-0"
              data-testid={`public-content-download-progress-${video.id}`}
            >
              <Progress value={downloadProgress} className="h-2" />
              <p
                className="mt-1 text-[11px] text-[var(--ns-text-muted)]"
                data-testid={`public-content-download-progress-status-${video.id}`}
              >
                {downloadStatus === 'preparing' && 'Preparando descarga…'}
                {downloadStatus === 'downloading' && `Descargando ${downloadProgress} %`}
                {downloadStatus === 'completed' && 'Descarga completada'}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

// Pestaña segmentada compacta — Todos / Vídeos / Estadísticas.
function ContentsFilterTabs({ counts, value, onChange }) {
  const items = [
    { key: 'all', label: 'Todos', icon: null, count: counts.all },
    { key: 'videos', label: 'Vídeos', icon: Film, count: counts.videos },
    { key: 'stats', label: 'Estadísticas', icon: BarChart3, count: counts.stats },
  ];
  return (
    <div
      className="inline-flex items-center gap-1 rounded-xl bg-[var(--ns-bg-2)] p-1 border border-[var(--ns-border)]"
      role="tablist"
      data-testid="public-contents-filter"
    >
      {items.map((it) => {
        const active = value === it.key;
        const Icon = it.icon;
        return (
          <button
            key={it.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.key)}
            disabled={it.count === 0}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold transition-colors ${
              active
                ? 'bg-[var(--ns-gold)] text-white shadow-sm'
                : 'text-[var(--ns-text-muted)] hover:text-[var(--ns-text)] hover:bg-white/60 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
            data-testid={`public-contents-filter-${it.key}`}
          >
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
            {it.label} <span className="opacity-80">({it.count})</span>
          </button>
        );
      })}
    </div>
  );
}

export function EventContentsSection({ eventSlug, eventId, previewVideoUrl, posterUrl, videos, refresh }) {
  const { user } = useAuth();
  const publishedVideos = (videos || []).filter(
    (v) => ['published', 'replay_available'].includes(v.status),
  );
  const [tab, setTab] = React.useState('all');
  // Fase 06D — mapa `entitlement_id -> item` consumido por `downloadCTA`
  // para derivar los estados preparing / ready / exhausted. Sólo se pide
  // si hay sesión; cualquier error se silencia para no romper la ficha.
  const [myDownloadsByEnt, setMyDownloadsByEnt] = React.useState({});
  React.useEffect(() => {
    let cancelled = false;
    if (!user) { setMyDownloadsByEnt({}); return () => { cancelled = true; }; }
    (async () => {
      try {
        const { items = [] } = (await fetchMyDownloads()) || {};
        if (cancelled) return;
        const map = {};
        for (const it of (items || [])) {
          if (it?.entitlement_id) map[it.entitlement_id] = it;
        }
        setMyDownloadsByEnt(map);
      } catch (err) {
        // Hacemos visibles futuros errores sin bloquear la vista pública.
        console.error('[EventContentsSection] fetchMyDownloads failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user, eventSlug]);

  const stats = publishedVideos.filter((v) => v.content_kind === 'statistics');
  const vids = publishedVideos.filter((v) => v.content_kind !== 'statistics');
  const counts = {
    all: publishedVideos.length,
    videos: vids.length,
    stats: stats.length,
  };
  const visible = tab === 'videos' ? vids : tab === 'stats' ? stats : publishedVideos;

  if (!eventSlug) return null;

  return (
    <section
      id="contenidos"
      className="mt-8"
      data-testid="event-contents-section"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display uppercase text-2xl sm:text-3xl text-[var(--ns-text)] leading-none">
            Contenidos del partido
          </h2>
          <p className="text-xs text-[var(--ns-text-muted)] mt-1">
            {publishedVideos.length} {publishedVideos.length === 1 ? 'contenido' : 'contenidos'}
          </p>
        </div>
        {publishedVideos.length > 0 ? (
          <ContentsFilterTabs counts={counts} value={tab} onChange={setTab} />
        ) : null}
      </div>

      {publishedVideos.length === 0 ? (
        <Card
          className="p-6 text-center text-sm text-[var(--ns-text-muted)]"
          data-testid="event-contents-empty"
        >
          Aún no hay contenidos disponibles para este partido.
        </Card>
      ) : visible.length === 0 ? (
        <Card
          className="p-6 text-center text-sm text-[var(--ns-text-muted)]"
          data-testid="event-contents-empty-filter"
        >
          No hay contenidos en esta categoría.
        </Card>
      ) : (
        <div className="space-y-2.5">
          {visible.map((v) => (
            <ContentCard
              key={v.id}
              video={v}
              eventSlug={eventSlug}
              eventId={eventId}
              previewVideoUrl={previewVideoUrl}
              posterUrl={posterUrl}
              refresh={refresh}
              myDownloadsByEnt={myDownloadsByEnt}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default EventContentsSection;
